import { mic } from 'icons/mic.js';
import { micActive } from 'icons/micActive.js';

import { CSS_COLORS, KEY_CODES } from 'consts/consts';
import { MESSAGE_ACTION, MESSAGE_SENDER, MESSAGE_TYPE } from 'consts/enum';
import { LS } from 'consts/localStorage';
import {
	addClass,
	disableInputArea,
	enableInputArea,
	generateUUID,
	hasClass,
	LOGGER,
	readFromLs,
	removeClass,
	removeElement,
	setCustomTagHTML,
	writeToLs,
} from 'helpers/helpers';
import { disconnect, emitForUserCreationInLaunchScreen, emitMessage, socketInit } from 'helpers/socket';
import { ENGT_CHAT } from 'js/bot.model';
import { appendToChat, generateStartConvButton, removeQuote } from 'js/conversation';
import { recorder } from 'js/speech.js';

import { MAX_CHARACTER_LIMIT, REGEX_EQUATION_TO_CHECK_FOR_HTML_TAG } from '../../consts/consts';
import { FileAttachments } from './components/attachments/attachments.module';
import { FaqAutocomplete } from './components/faqAutocomplete/faqAutocomplete.module.js';
import { Menu } from './components/menu/menu.module';
import { ERROR_MESSAGE, replyInput } from './replyInput.model';
import { ReplyInputTemplate } from './replyInput.template';

const ERROR_TIMEOUT = 3000;
export class ReplyInput {
	constructor(props) {
		this.initData(props);
		this.initWrapper();
		this.initComponents();
		this.initEvents();
		this.initHelpers();
	}

	initData(props) {
		this.data = { ...replyInput };
		this.setter({
			...props,
			theme: ENGT_CHAT.get('config.themeColor'),
			bgColor: ENGT_CHAT.get('config.textAreaColor'),
			placeholder: ENGT_CHAT.get('config.textAreaPlaceholder') || replyInput.placeholder,
			showReplyArea: ENGT_CHAT.get('config.showReplyArea'),
			isVoiceEnabled:
				ENGT_CHAT.get('speech.allowSpeechRecognition') || ENGT_CHAT.get('speech.allowAudioConversation'),
			isMenuConfigured: true,
			isFaqAutocompleteEnabled: ENGT_CHAT.get('config.isFaqAutocompleteEnabled'),
		});
	}

	initWrapper() {
		const { bgColor } = this.data;
		const wrapper = document.createElement('div');
		wrapper.className = 'engt-reply-box-wrapper';
		wrapper.style = `background: ${bgColor};`;
		wrapper.innerHTML = ReplyInputTemplate(this.data);

		this.html = wrapper;
	}

	initComponents() {
		const { showReplyArea } = this.data;
		this.messageArea = this.html.querySelector('#engtUserMessage');
		this.menuBtn = this.html.querySelector('#engtMenuBtn');
		this.faqAutocompleteBtnOn = this.html.querySelector('#engtFaqAutocompleteBtnOn');
		this.faqAutocompleteBtnOff = this.html.querySelector('#engtFaqAutocompleteBtnOff');
		this.voiceInputBtn = this.html.querySelector('#engtVoiceInputBtn');
		this.menuWrapper = document.querySelector('#engtMenuWrapper');
		this.sendBtn = this.html.querySelector('#engtSendBtn');
		this.attachmentPreview = document.createElement('div');
		this.attachmentPreview.id = 'replyAttachmentPreview';
		this.faqAutocompleteInstance = new FaqAutocomplete();

		if (showReplyArea === false && readFromLs(LS().LIVECHAT) !== 'true') {
			this.hideInputBox();
		} else {
			this.showInputBox();
		}

		setCustomTagHTML(
			this.html.querySelector('file-attachment'),
			new FileAttachments({
				wrapperId: 'replayAreaUpload',
				contentId: 'replayAreaUploadContent',
				content: this.attachmentPreview,
				contentCss: 'background-color: transparent',
			}).render()
		);
	}

	initEvents() {
		const ref = this;
		this.chatSetting = this.html.querySelector('#chatSetting');

		this.messageArea.addEventListener('keyup', function (event) {
			const hasInputText = this.innerText?.trim?.();
			const sock_msg = {
				bot_key: ENGT_CHAT.get('config.bot_key'),
				user_id: ENGT_CHAT.get('status.sessionKey'),
				sender_action: 'typing_on',
				key: ENGT_CHAT.get('config.key'),
			};

			emitMessage('status_packet', sock_msg);
			ref.toggleSendBtn(hasInputText);
			if (hasInputText) {
				ENGT_CHAT.get('onEnterPress')(() => {
					if (ENGT_CHAT.get('isAllInputsDisabled')) {
						return;
					}
					const message = ref.messageArea?.innerText;
					if (checkAndDisplayError(message)) {
						return;
					}
					ENGT_CHAT.get('sendMessage')(this.innerText.trim());
					this.innerText = '';
					ref.toggleSendBtn('');
				});
			}
		});

		this.messageArea.addEventListener('keypress', (event) => {
			if (event.keyCode === 13 && !event.shiftKey) {
				event.preventDefault();
			}
		});

		this.messageArea.addEventListener('input', (e) => {
			if (
				ref.data.isFaqAutocompleteEnabled == 'INDEXED' ||
				ref.data.isFaqAutocompleteEnabled == 'PARTIALLY_INDEXED'
			) {
				const locale = ENGT_CHAT.get('config.botLanguage');
				const botRef = ENGT_CHAT.get('config.botRef');
				const indexUid = `${botRef}_Index_faqs_${locale}`;
				const input = ref.messageArea.innerText;
				const cleanedInput = input.replace(/^[^a-zA-Z0-9]+/, '');
				if (cleanedInput?.trim()) {
					ref.faqAutocompleteInstance.fetchAutocompleteResults(cleanedInput, indexUid);
				}
			}
		});

		this.messageArea.addEventListener('paste', (e) => {
			e.preventDefault();
			const text = e.clipboardData.getData('text/plain');
			document.execCommand('insertText', false, text);
		});

		this.menuBtn?.addEventListener('click', ref.reRenderMenu.bind(this));

		if (!ENGT_CHAT.get('config.isConversationalIvrEnabled')) {
			this?.voiceInputBtn?.addEventListener('click', this.renderVoiceInput);
		}

		this.faqAutocompleteBtnOn?.addEventListener('click', () => {
			ref.faqAutocompleteInstance?.closeFaqAutocompletePopUp();
			ENGT_CHAT.set('isFaqAcPopUpClose', true);
		});
		this.faqAutocompleteBtnOff?.addEventListener('click', () => {
			ref.faqAutocompleteInstance?.openFaqAutocompletePopUp();
			ENGT_CHAT.set('isFaqAcPopUpClose', false);
		});

		function displayErrorMessage(message) {
			const errorMessage = `${message}`;
			const errorDiv = document.getElementById('engtInputError');
			if (errorDiv) {
				errorDiv.innerHTML = errorMessage;
				errorDiv.style.display = 'block';
				setTimeout(() => {
					errorDiv.style.display = 'none';
				}, ERROR_TIMEOUT);
			}
		}

		function checkAndDisplayError(message) {
			if (message?.length > MAX_CHARACTER_LIMIT) {
				displayErrorMessage(
					ENGT_CHAT.get('GENERIC_STRINGS')?.['CHARACTER_LIMIT_EXCEEDED'] ||
						ERROR_MESSAGE.characterLimitExceeded
				);
				return true;
			}
			const regex = REGEX_EQUATION_TO_CHECK_FOR_HTML_TAG;
			if (regex.test(message)) {
				displayErrorMessage(
					ENGT_CHAT.get('GENERIC_STRINGS')?.['HTML_TAG_NOT_ALLOWED'] || ERROR_MESSAGE.htmlTagNotAllowed
				);
				return true;
			}
			return false;
		}

		this.sendBtn.addEventListener('click', () => {
			if (ENGT_CHAT.get('isAllInputsDisabled')) {
				return;
			}
			const message = ref.messageArea?.innerText;
			if (checkAndDisplayError(message)) {
				return;
			}
			ENGT_CHAT.get('sendMessage')(ref.messageArea.innerText);
			ref.toggleSendBtn('');
		});
	}

	hideInputBox() {
		this.html.style.cursor = 'not-allowed';
		this.html.querySelector('#engtReplyInput').style.visibility = 'hidden';
	}

	showInputBox() {
		this.html.style.cursor = null;
		this.html.querySelector('#engtReplyInput').style.visibility = null;
	}

	initHelpers() {
		ENGT_CHAT.set('sendMessage', this.sendMessage);
		ENGT_CHAT.set('sendAttachment', this.sendAttachment);
		ENGT_CHAT.set('menuClose', this.menuClose.bind(this));
		ENGT_CHAT.set('renderReplyInput', this.renderReplyInput.bind(this));
		ENGT_CHAT.set('renderStartConversation', this.renderStartConversation.bind(this));
		ENGT_CHAT.set('disableAllInput', this.disableAllInput.bind(this));
		ENGT_CHAT.set('enableAllInput', this.enableAllInput.bind(this));
		ENGT_CHAT.set('hideInputBox', this.hideInputBox.bind(this));
		ENGT_CHAT.set('showInputBox', this.showInputBox.bind(this));
	}

	setter(propData) {
		this.data = {
			...this.data,
			...propData,
		};
	}

	toggleSendBtn(showSendBtn) {
		const ref = this;
		const allReplyAreaBtns = this.html.querySelectorAll('.engt-reply-btns');
		const faqAutocompleteWrapper = document.querySelector('#engtFaqAutocompleteWrapper');
		if (showSendBtn) {
			if (allReplyAreaBtns.length) {
				for (let i = 0; i < allReplyAreaBtns.length; i++) {
					const btn = allReplyAreaBtns[i];
					if (btn.id !== 'engtSendBtn') {
						btn.classList.add('engt-hidden');
					}
				}
			}
			ref.sendBtn.style.display = 'flex';
			ref.sendBtn.classList.add('engt-append-icon');
			setTimeout(() => {
				ref.sendBtn.classList.remove('engt-hidden');
			}, 200);
		} else if (ref.sendBtn) {
			ref.sendBtn.style.display = 'none';
			ref.sendBtn.style.transition = 'opacity 0.3s ease-out';
			ref.sendBtn.style.opacity = 0;
			setTimeout(() => {
				ref.sendBtn.style.opacity = 1;
				ref.sendBtn.classList.add('engt-hidden');
				if (allReplyAreaBtns.length) {
					for (let i = 0; i < allReplyAreaBtns.length; i++) {
						const btn = allReplyAreaBtns[i];
						btn.classList.remove('engt-hidden');
					}
				}
				if (ref.data.isFaqAutocompleteEnabled) {
					if (ref.faqAutocompleteBtnOn) ref.faqAutocompleteBtnOn.classList.add('engt-hidden');
					if (ref.faqAutocompleteBtnOff) ref.faqAutocompleteBtnOff.classList.add('engt-hidden');
					if (faqAutocompleteWrapper) removeElement(faqAutocompleteWrapper);
				}
			}, 200);
		}
	}

	disableAllInput() {
		if (ENGT_CHAT.get('isAllInputsDisabled')) {
			return;
		}
		addClass(document.getElementById('engtReplyInput'), 'engt-visible');
		const inputBtns = this.html.querySelectorAll('.engt-reply-btns');
		const wrapper = document.querySelector('.engt-reply-box-wrapper');
		disableInputArea();
		if (wrapper?.classList) {
			wrapper.classList.add('engt-reply-box-wrapper-disabled');
		}
		inputBtns.forEach((inputBtn) => {
			inputBtn.disabled = true;
		});

		ENGT_CHAT.set('isAllInputsDisabled', true);
	}

	enableAllInput() {
		if (!ENGT_CHAT.get('isAllInputsDisabled')) {
			return;
		}
		removeClass(document.getElementById('engtReplyInput'), 'engt-visible');
		const inputBtns = this.html.querySelectorAll('.engt-reply-btns');
		const wrapper = document.querySelector('.engt-reply-box-wrapper');
		enableInputArea();
		if (wrapper?.classList) {
			wrapper.classList.remove('engt-reply-box-wrapper-disabled');
		}
		inputBtns.forEach((inputBtn) => {
			inputBtn.disabled = false;
		});

		ENGT_CHAT.set('isAllInputsDisabled', false);
	}

	sendMessage(msg, config) {
		const disableSmoOptions = function () {
			const smoEnabledBtns = document?.querySelectorAll(
				'.engt-msg-smo button:not([disabled]), .engt-msg-smo input:not([disabled])'
			);
			if (smoEnabledBtns.length) {
				smoEnabledBtns.forEach((smoBtn) => {
					if (ENGT_CHAT.get('activeSmoDropdown') && smoBtn.hasAttribute('autocomplete')) {
						if (!config) {
							smoBtn.disabled = true;
						}
					} else {
						smoBtn.disabled = true;
					}
				});
			}
		};
		if (msg?.trim?.() || config?.message) {
			if (readFromLs(LS().GOT_UID) === 'true') {
				if (ENGT_CHAT.get('config.disableSmoOptions')) {
					disableSmoOptions();
				}
				const generatedMessageId = `${ENGT_CHAT.get('config.botRef')}_${generateUUID()}`;
				const ackMessageMap = { ...(ENGT_CHAT.get('ackMessageMap') || {}) };
				const ack = ENGT_CHAT.get('ack') + 1;
				const ackCopy = ack;
				const msgBody = {
					bot_key: ENGT_CHAT.get('config.bot_key'),
					user_id: ENGT_CHAT.get('status.sessionKey'),
					ack,
					key: ENGT_CHAT.get('config.key'),
					message_id: config?.message_id ? config.message_id : generatedMessageId,
				};
				if (ENGT_CHAT.get('quotedMsgDetails')) {
					msgBody['linked_response_data'] = ENGT_CHAT.get('quotedMsgDetails').linked_response_data;
					msgBody['linked_event_id'] = ENGT_CHAT.get('quotedMsgDetails').linked_event_id;
					removeQuote();
				}
				const data = {
					sender: MESSAGE_SENDER.USER,
					message: {
						sender: MESSAGE_SENDER.USER,
						text: msg,
						isPassword: config?.isPassword || false,
					},
					timestamp: new Date().toISOString(),
				};
				ENGT_CHAT.get('hideMinimizedCallout')?.();

				emitMessage(
					'user_msg',
					{
						...msgBody,
						is_debug: ENGT_CHAT.get('config.is_debug'),
						timestamp: new Date().toISOString(),
						message: config?.message || { type: 'text', text: msg },
						...(config && 'attributes' in config && { attributes: config.attributes }),
					},
					(ackCopy) => {
						LOGGER(`Got server ack: ${ackCopy}`);

						delete ackMessageMap[ackCopy];
						ENGT_CHAT.set('ackMessageMap', ackMessageMap);
					}
				);
				emitForUserCreationInLaunchScreen();
				ENGT_CHAT.set('isFirstMessage', false);

				ackMessageMap[ack] = { message: msg, timestamp: Date.now(), retryCount: 0 };
				clearTimeout(ENGT_CHAT.get('config')?.ackTimeout);

				ENGT_CHAT.set(
					'config.ackTimeout',
					setTimeout(() => {
						if (ackMessageMap[ackCopy]) {
							disconnect();
							LOGGER('disconnected and connecting again.');
						}
					}, 5000)
				);

				if (!config?.skipAppendToDom) {
					const localStorageMsg = { ...data, ...msgBody };
					if (config?.tab_msg_action != 'skip') {
						writeToLs(
							LS().TAB_MESSAGES,
							JSON.stringify({
								...localStorageMsg,
								istabMsg: true,
								action: config?.tab_msg_action || 'add_user_msg',
								tabId: ENGT_CHAT.get('socketId'),
								skipAppendToDom: config?.skipAppendToDom || false,
							})
						);
					}

					data.message.text && appendToChat({ ...data, ...msgBody });
				}
				if (ENGT_CHAT.get('config.isEnhancedTypingIndicatorEnabled')) {
					const typingData = {
						message: {
							action: MESSAGE_ACTION.TYPING_ON,
							type: MESSAGE_TYPE.TYPING_INDICATOR,
						},
						timestamp: new Date().toISOString(),
					};
					ENGT_CHAT.get('showTypingIndicator')(typingData, 'engt-typing-indicator');
				}
			} else if (!ENGT_CHAT.get('triggerButtonConfig')?.isFlowTriggeredFromTriggers) {
				disconnect();
				socketInit();
			}
		}
	}

	menuClose(e = event, forceClose = false) {
		this.menuWrapper = document.querySelector('#engtMenuWrapper');

		if (!ENGT_CHAT.get('isMenuOpen')) {
			return;
		}
		if (
			(forceClose && this.menuWrapper) ||
			(e && !(this.menuBtn?.contains(e.target) || this.menuWrapper?.contains(e.target)))
		) {
			this.menuWrapper.classList.remove('engt-menu-wrapper-animate');
			setTimeout(() => {
				ENGT_CHAT.set('isMenuOpen', false);
				this.menuWrapper.remove();
				this.menuBtn.style = '';
			}, 500);
		}
	}

	reRenderMenu() {
		if (ENGT_CHAT.get('isAllInputsDisabled')) {
			return;
		}

		const isMenuOpen = ENGT_CHAT.get('isMenuOpen');
		ENGT_CHAT.get('hideMinimizedCallout')?.();
		ENGT_CHAT.set('isMenuOpen', !isMenuOpen);

		if (isMenuOpen) {
			this.menuWrapper.classList.remove('engt-menu-wrapper-animate');
			this.menuWrapper.removeEventListener('keydown', this.listenKeydownInMenu);
			setTimeout(() => {
				this.menuWrapper.remove();
				this.menuBtn.style = '';
			}, 500);
		} else {
			const menu = new Menu();
			document.getElementById('engtChatWrapper').insertAdjacentHTML('beforeend', menu.render());

			this.menuWrapper = document.querySelector('#engtMenuWrapper');
			this.menuWrapper.addEventListener('keydown', this.listenKeydownInMenu);
			this.menuBtn.style = `stroke: ${ENGT_CHAT.get('config.themeColor')}`;
			this.menuWrapper.querySelector('.engt-menu-options-wrapper').children[0].focus();
		}
	}

	renderVoiceInput() {
		if (ENGT_CHAT.get('inputMode') !== 'input') {
			recorder.changeInputMode('input');
			return;
		}

		navigator.permissions
			.query({ name: 'microphone' })
			.then((permissionStatus) => {
				if (permissionStatus.state === 'granted') {
					const replyInputArea = document.querySelector('.engt-reply-area');
					const voiceInput = document.querySelector('#engt-non-input-mode');
					const engtVoiceWrapper = document.getElementById('engtVoiceWrapper');
					const engtVoiceInputBtn = document.getElementById('engtVoiceInputBtn');

					if (hasClass(replyInputArea, 'engt-hidden')) {
						voiceInput.style.display = 'none';
						replyInputArea.classList.remove('engt-hidden');
						engtVoiceWrapper.classList.remove('engtVoiceWrapper');
						engtVoiceInputBtn.innerHTML = micActive();
					} else {
						replyInputArea.classList.add('engt-hidden');
						engtVoiceWrapper.classList.add('engtVoiceWrapper');
						voiceInput.style.display = 'block';
						engtVoiceInputBtn.innerHTML = mic(CSS_COLORS.THEME_COLOR);
					}
					recorder.changeInputMode('listening');
				} else if (permissionStatus.state === 'prompt') {
					navigator.mediaDevices.getUserMedia({ audio: true }).catch((error) => {
						console.error('Please enable microphone permissions in your browser settings:', error);
					});
				} else if (permissionStatus.state === 'denied') {
					alert(
						'Microphone access is currently denied. Please enable it in your browser settings to continue.'
					);
				}
			})
			.catch((error) => {
				console.error('Permission query error:', error);
			});
	}

	renderStartConversation() {
		const footer = document.querySelector('respond-area');
		footer.replaceChildren(generateStartConvButton());
	}

	renderReplyInput() {
		this.html.remove();
		this.initWrapper();
		this.initComponents();
		this.initEvents();
		this.initHelpers();
		const footer = document.querySelector('respond-area');
		footer.replaceChildren(this.html);
	}

	listenKeydownInMenu = (e) => {
		const keyCode = e.which ? e.which : e.keyCode;

		if (keyCode === KEY_CODES.ESC) {
			this.menuBtn.click();
		}

		if (keyCode === KEY_CODES.TAB) {
			const options = this.menuWrapper?.querySelector?.('.engt-menu-options-wrapper')?.children || [];

			if (options.length) {
				if (document.activeElement === options[options.length - 1]) {
					e.stopPropagation();
					e.preventDefault();
					options[0].focus();
				}
			}
		}
	};

	render() {
		return this.html;
	}
}
